// src/pages/CoffeeQuote.jsx
import React, { useRef, useMemo } from "react";
import { useReactToPrint } from "react-to-print";
import { quoteData } from "../data/quoteData-Coffee.js";

// ---------- Header ----------
function Header({
  title,
  clientName,
  date,
  auditedBy,
  preparedBy,
  url,
  quoteNumber,
  business = {},
}) {
  const { name, abn, email, phone } = business;
  return (
    <header className="bg-[#0E1526] text-white rounded-lg p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 print:rounded-none">
      <div className="space-y-1">
        <div className="text-2xl font-semibold leading-none">
          <span className="text-white">2</span>
          <span className="text-blue-400">merative</span>
        </div>
        <div className="uppercase tracking-widest text-xs text-gray-300">
          Iterate. Create. Elevate.
        </div>
        {(name || abn || email || phone) && (
          <div className="text-xs text-gray-400 space-y-0.5 mt-2">
            {name && <div>{name}</div>}
            {abn && <div>ABN: {abn}</div>}
            {email && <div>{email}</div>}
            {phone && <div>{phone}</div>}
          </div>
        )}
      </div>

      <div className="text-right space-y-1 md:min-w-[280px]">
        {title && <div className="text-2xl font-semibold">{title}</div>}
        {clientName && <div className="text-sm text-gray-300">[{clientName}]</div>}
        <div className="text-xs text-gray-400 mt-1 space-y-0.5">
          {quoteNumber && <div>Quote #: {quoteNumber}</div>}
          {date && <div>Date: {date}</div>}
          {preparedBy && <div>Prepared by: {preparedBy}</div>}
          {auditedBy && <div>Audited by: {auditedBy}</div>}
          {url && <div>URL: {url}</div>}
        </div>
      </div>
    </header>
  );
}

// ---------- Recurring Costs Note ----------
const RecurringNote = () => (
  <p className="text-xs text-gray-500 mt-2">
    * Monthly and yearly costs are recurring charges for hosting, SSL, and basic maintenance (
    <span className="font-semibold">Year 1 included in one-off quote</span>).
  </p>
);

// ---------- Helpers ----------
const currency = (n) =>
  (Number(n) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

const calcTotals = (sections = {}, gstRate = 0.1) => {
  const oneOffArr = Array.isArray(sections.oneOff) ? sections.oneOff : [];
  const recurArr = Array.isArray(sections.recurring) ? sections.recurring : [];

  const sum = (arr, key) => arr.reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0);

  const oneOffEx = sum(oneOffArr, "cost");
  const recurringMonthlyEx = sum(recurArr, "costMonthly");
  const recurringYearlyEx = sum(recurArr, "costYearly");

  const gstOneOff = oneOffEx * gstRate;
  const gstMonthly = recurringMonthlyEx * gstRate;
  const gstYearly = recurringYearlyEx * gstRate;

  return {
    oneOffEx,
    recurringMonthlyEx,
    recurringYearlyEx,
    gstOneOff,
    gstMonthly,
    gstYearly,
    oneOffInc: oneOffEx + gstOneOff,
    recurringMonthlyInc: recurringMonthlyEx + gstMonthly,
    recurringYearlyInc: recurringYearlyEx + gstYearly,
  };
};

// ---------- Main ----------
export default function CoffeeQuote() {
  const contentRef = useRef();
  const meta = quoteData?.meta ?? {};
  const business = quoteData?.business ?? {};
  const gstRate = typeof quoteData?.gstRate === "number" ? quoteData.gstRate : 0.1;
  const notes = Array.isArray(quoteData?.notes) ? quoteData.notes : [];
  const assumptions = Array.isArray(quoteData?.assumptionsExclusions)
    ? quoteData.assumptionsExclusions
    : [];
  const warranty = Array.isArray(quoteData?.warrantySupport)
    ? quoteData.warrantySupport
    : [];
  const acceptance = quoteData?.acceptance ?? {};
  const options = Array.isArray(quoteData?.options) ? quoteData.options : [];

  const totals = useMemo(
    () =>
      options.map((opt) => ({
        label: opt?.label ?? "Untitled Option",
        ...calcTotals(opt?.sections, gstRate),
      })),
    [options, gstRate]
  );

  // react-to-print v3: use contentRef
  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: meta.quoteNumber || meta.title || "Quote",
  });

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div ref={contentRef} className="bg-white rounded-lg shadow">
        <Header
          title={meta.title}
          clientName={meta.clientName}
          date={meta.date}
          auditedBy={meta.auditedBy}
          preparedBy={meta.preparedBy}
          url={meta.url}
          quoteNumber={meta.quoteNumber}
          business={business}
        />

        {/* Summary from data */}
        {Array.isArray(meta.summary) && meta.summary.length > 0 && (
          <section className="p-6 border-b border-gray-200 text-sm">
            <h3 className="font-semibold text-gray-700 mb-2">Summary</h3>
            {meta.summary.map((para, i) => (
              <p key={i} className="mb-2">{para}</p>
            ))}
          </section>
        )}

        {/* Prepared For / By */}
        <section className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Prepared For</h3>
            {meta.clientName && <div>{meta.clientName}</div>}
            {meta.url && <div>{meta.url}</div>}
            {meta.quoteNumber && <div>Quote #: {meta.quoteNumber}</div>}
            {meta.date && <div>Date: {meta.date}</div>}
          </div>
          <div className="md:text-right">
            <h3 className="font-semibold text-gray-700 mb-2">Prepared By</h3>
            {business.name && <div>{business.name}</div>}
            {business.abn && <div>ABN: {business.abn}</div>}
            {business.email && <div>{business.email}</div>}
            {business.phone && <div>{business.phone}</div>}
            {meta.preparedBy && <div>Consultant: {meta.preparedBy}</div>}
          </div>
        </section>

        {/* Options */}
        {options.map((opt, idx) => {
          const t = totals[idx];
          const oneOff = opt?.sections?.oneOff ?? [];
          const recurring = opt?.sections?.recurring ?? [];
          const approach = opt?.sections?.projectApproach ?? [];

          return (
            <section key={opt?.label ?? idx} className="p-6 border-t border-gray-200">
              <h2 className="text-xl font-bold mb-4 border-l-4 border-blue-600 pl-3">
                {opt?.label ?? "Untitled Option"}
              </h2>

              {/* Project Approach */}
              {approach.length > 0 && (
                <div className="mb-4 text-sm">
                  <h4 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
                    Project Approach
                  </h4>
                  <ul className="list-disc ml-5 space-y-1">
                    {approach.map((a, i) => (
                      <li key={i}>{a}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* One-off */}
              <h3 className="font-semibold text-gray-700 mt-4 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
                One-off Costs (ex GST)
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2">Item</th>
                    <th className="py-2 text-right">Cost (ex GST)</th>
                  </tr>
                </thead>
                <tbody>
                  {oneOff.map((row, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2">{row?.item ?? "Untitled"}</td>
                      <td className="py-2 text-right">{currency(row?.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Recurring */}
              <h3 className="font-semibold text-gray-700 mt-6 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
                Recurring Costs (ex GST)
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2">Item</th>
                    <th className="py-2 text-right">Monthly (ex)</th>
                    <th className="py-2 text-right">Yearly (ex)</th>
                  </tr>
                </thead>
                <tbody>
                  {recurring.map((row, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2">{row?.item ?? "Untitled"}</td>
                      <td className="py-2 text-right">
                        {row?.costMonthly ? currency(row.costMonthly) : "-"}
                      </td>
                      <td className="py-2 text-right">
                        {row?.costYearly ? currency(row.costYearly) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 p-4 rounded shadow-sm">
                  <h4 className="font-semibold mb-2 text-blue-700">Totals (ex GST)</h4>
                  <div className="flex justify-between"><span>One-off:</span><span>{currency(t.oneOffEx)}</span></div>
                  <div className="flex justify-between"><span>Monthly (Recurring):</span><span>{currency(t.recurringMonthlyEx)}</span></div>
                  <div className="flex justify-between"><span>Yearly (Recurring):</span><span>{currency(t.recurringYearlyEx)}</span></div>
                </div>
                <div className="bg-gray-50 p-4 rounded shadow-sm">
                  <h4 className="font-semibold mb-2 text-blue-700">Totals (incl. GST)</h4>
                  <div className="flex justify-between"><span>One-off:</span><span>{currency(t.oneOffInc)}</span></div>
                  <div className="flex justify-between"><span>Monthly (Recurring, inc GST):</span><span>{currency(t.recurringMonthlyInc)}</span></div>
                  <div className="flex justify-between"><span>Yearly (Recurring, inc GST):</span><span>{currency(t.recurringYearlyInc)}</span></div>
                </div>
              </div>

              <RecurringNote />
            </section>
          );
        })}

        {/* Notes */}
        {notes.length > 0 && (
          <section className="p-6 border-t border-gray-200 text-sm">
            <h3 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">Notes</h3>
            <ul className="list-disc ml-5 space-y-1">
              {notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Assumptions & Exclusions */}
        {assumptions.length > 0 && (
          <section className="p-6 border-t border-gray-200 text-sm">
            <h3 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">Assumptions & Exclusions</h3>
            <ul className="list-disc ml-5 space-y-1">
              {assumptions.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Warranty & Support */}
        {warranty.length > 0 && (
          <section className="p-6 border-t border-gray-200 text-sm">
            <h3 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">Warranty & Support</h3>
            <ul className="list-disc ml-5 space-y-1">
              {warranty.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Acceptance */}
        <section className="p-6 border-t border-gray-200 text-sm">
          <h3 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">Acceptance</h3>
          {acceptance?.validUntil && <div>Valid until: {acceptance.validUntil}</div>}
          {acceptance?.paymentTerms && <div>Payment terms: {acceptance.paymentTerms}</div>}
          <div className="mt-6 h-24 border rounded p-4">
            Signature: __________________________________________
          </div>
          <div className="mt-2">Name: __________________________________________</div>
          <div className="mt-2">Date: ___________________________________________</div>
        </section>
      </div>

      {/* Print Button */}
      <div className="mt-6 flex justify-end print:hidden">
        <button
          onClick={handlePrint}
          className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
        >
          Print / Save as PDF
        </button>
      </div>
    </div>
  );
}
